import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, Compass, Megaphone, ShoppingBag, MessageCircle, Sparkles, LogOut, Zap } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const links = [
  { to: "/feed", label: "Feed", icon: Home, id: "feed" },
  { to: "/discover", label: "Discover", icon: Compass, id: "discover" },
  { to: "/campaigns", label: "Campaigns", icon: Megaphone, id: "campaigns" },
  { to: "/marketplace", label: "Marketplace", icon: ShoppingBag, id: "marketplace" },
  { to: "/messages", label: "Messages", icon: MessageCircle, id: "messages" },
  { to: "/ai-studio", label: "AI Studio", icon: Sparkles, id: "ai-studio" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-pink-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/feed" className="flex items-center gap-2" data-testid="navbar-logo">
          <div className="h-9 w-9 rounded-xl brand-gradient flex items-center justify-center shadow-lg shadow-pink-500/30">
            <Zap className="h-5 w-5 text-white" fill="white" />
          </div>
          <span className="font-display font-extrabold text-xl tracking-tight hidden sm:block">MrktMe</span>
        </Link>

        <div className="flex items-center gap-1 overflow-x-auto">
          {links.map((l) => {
            const active = loc.pathname === l.to;
            const Icon = l.icon;
            return (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                  active ? "bg-pink-500 text-white shadow-md shadow-pink-500/30" : "text-slate-600 hover:bg-pink-50 hover:text-pink-600"
                }`}>
                <Icon className="h-4 w-4" strokeWidth={2.4} />
                <span className="hidden lg:block">{l.label}</span>
              </Link>
            );
          })}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger data-testid="navbar-avatar" className="outline-none">
            <Avatar className="h-9 w-9 ring-2 ring-pink-200 hover:ring-pink-400 transition-all">
              <AvatarImage src={user?.avatar} />
              <AvatarFallback className="bg-fuchsia-100 text-fuchsia-700 font-bold text-sm">{initials}</AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 rounded-xl">
            <div className="px-2 py-1.5">
              <p className="font-semibold text-sm truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav(`/profile/${user.id}`)} data-testid="menu-profile">My Profile</DropdownMenuItem>
            <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout" className="text-rose-600">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
}
